import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { PageTitle, MediaField, TextField } from "./AdminKit";
import { api, fileUrl, formatErr, useCategories } from "../../lib/api";

const EMPTY = { name: "", slug: "", tagline: "", description: "", image: "", order: 0 };

export const CategoriesAdmin = () => {
  const qc = useQueryClient();
  const { categories } = useCategories();
  const [edit, setEdit] = useState(null);
  const set = (k, v) => setEdit((p) => ({ ...p, [k]: v }));
  const refresh = () => { qc.invalidateQueries({ queryKey: ["categories"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); };

  const save = async () => {
    try {
      const body = { ...edit, order: Number(edit.order) || 0 };
      if (edit.id) await api.put(`/admin/categories/${edit.id}`, body);
      else await api.post("/admin/categories", body);
      toast.success("Category saved"); setEdit(null); refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try { await api.delete(`/admin/categories/${c.id}`); toast.success("Deleted"); refresh(); } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div>
      <PageTitle title="Categories" desc="Product families shown on the website.">
        <button data-testid="admin-category-create-button" onClick={() => setEdit({ ...EMPTY, order: categories.length })} className="inline-flex items-center gap-2 bg-[#0a6cff] px-4 py-2 text-xs font-semibold text-white"><Plus className="h-4 w-4" />Add category</button>
      </PageTitle>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {categories.map((c) => (
          <div key={c.id} data-testid={`admin-category-${c.slug}`} className="flex items-center gap-4 bg-white p-4">
            <img src={fileUrl(c.image)} alt="" className="h-16 w-16 object-cover" />
            <div className="min-w-0 flex-1"><p className="truncate font-semibold text-slate-900">{c.name}</p><p className="text-xs text-slate-500">{c.product_count} products · order {c.order}</p></div>
            <button onClick={() => setEdit(c)} data-testid={`admin-category-edit-${c.slug}`} className="p-2 text-slate-500 hover:text-[#0a6cff]" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
            <button onClick={() => remove(c)} data-testid={`admin-category-delete-${c.slug}`} className="p-2 text-slate-500 hover:text-red-500" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      {edit && (
        <Dialog open onOpenChange={(v) => !v && setEdit(null)}>
          <DialogContent data-testid="admin-category-form" className="max-h-[90vh] max-w-lg overflow-y-auto rounded-none">
            <DialogTitle className="font-display text-3xl font-extrabold uppercase">{edit.id ? "Edit category" : "New category"}</DialogTitle>
            <div className="space-y-4">
              <TextField label="Name *" value={edit.name} onChange={(v) => set("name", v)} testId="admin-category-name" />
              <TextField label="Short tagline" value={edit.tagline} onChange={(v) => set("tagline", v)} testId="admin-category-tagline" />
              <TextField label="Description" textarea value={edit.description} onChange={(v) => set("description", v)} testId="admin-category-description" />
              <MediaField label="Cover image" value={edit.image} onChange={(v) => set("image", v)} testId="admin-category-image" />
              <div className="grid grid-cols-2 gap-4">
                <TextField label="Display order" type="number" value={edit.order} onChange={(v) => set("order", v)} testId="admin-category-order" />
                <TextField label="URL name (optional)" value={edit.slug} onChange={(v) => set("slug", v)} testId="admin-category-slug" />
              </div>
            </div>
            <button data-testid="admin-category-save" onClick={save} disabled={!edit.name} className="btn-electric disabled:opacity-50">Save category</button>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
